import LevelEconomyTracker from '../../Elements/GameTracking/LevelEconomyTracker';
import { LevelType } from '../../Elements/GameState/Managers/GameEnums';

/* eslint-disable prefer-destructuring */
/* eslint-disable no-useless-constructor */

/**
* Class for tracking mission event data for DDNA analytics
*/
class DDNAMissionTracker {
  /**
  * constructor
  */
  constructor() {
    //
  }

  /**
  * init mission parameters objects and set initial values/
  * @param {string} gameMode game mode CHALLENGE / NORMAL
  * @param {Object} extraData Extra data that needs to be added before event is sent
  */
  init(gameMode, extraData) {
    if (!DDNA.tracking.enabled) return;
    const levNum = gameMode === LevelType.CHALLENGE ? -1 : G.lvl.data.levelNumber;

    // parameters for all mission events
    this._missionStartParams = {
      missionName: `Level-${levNum}`,
      mLevel: levNum,
      mLevelDifficulty: G.lvl.data.difficulty * 1,
      mLevelHard: G.lvl.data.showAsHard ? 1 : 0,
      mRetryNum: G.saveState.getLevelRetries(G.lvl.data.levelIndex),
      mIsSuccessRetry: G.saveState.getStars(G.lvl.data.levelIndex) > 0 ? 1 : 0,
      mLevelMoves: G.lvl.moves,
      mIsDailyChallenge: levNum === -1 ? 1 : 0,
      mSpecialTileUsed: 0,
      mExtraMovesUsed: 0,
      mBombBoosterUsed: 0,
      mRequestHelpUniqueFriends: 0,
      mRequestHelpMessages: 0,
      mRequestHelpIsBot: 0,
      mRequestHelpBoosterForLevel: 0,
    };


    /**
     * MISSION PARAMETERS GO LIKE THIS
     * Start + Base + Fail = Fail event
     * Start + Base + Win = Win event
     */
    this._missionEndBaseParams = {
      mSwapBoosterUsed: 0,
      mRemoverBoosterUsed: 0,
      mHorizontalBoosterUsed: 0,
      mVerticalBoosterUsed: 0,
      mLevelExtraMovesUsed: 0,
      mCoinsSpent: 0,
      mCoinsBoughtWithRealMoney: 0,
      mBoostersBoughtWithRealMoney: 0,
      mNumBoostersUsedLossAversion: 0,
      mLossAversionCoinsSpent: 0,
      mMovesMade: 0,
      mMovesLeft: 0,
      mStars: 0,
      mScoreBeforeEndSequence: 0,
      mScore: 0,
      mCoinsLevelEnd: 0,
      mCoinsChests: 0,
      mAdsWatchedWheel: 0,
      mAdsWatchedDoubleReward: 0,
      mNumWheelSpins: 0,
      fortuneCookieTokenSpawn: 0,
      fortuneCookieTokenCollected: 0,
    };

    // parameters specific to missionComplete event. But missionFailParams will be appended on too
    this._missionWinParams = {
      fortuneCookieCategoryRejected: 0,
      fortuneCookieEventRejected: 0,
      fortuneCookieCategorySelected: 0,
      fortuneCookieShared: 0,
    };

    // parameters specific to missionFailed event. Only...
    this._missionFailParams = {
      // Empty
    };

    // listen for game events
    G.sb('onGoalAchieved').addOnce(() => { this.onGoalAchieved(); });
    G.sb('onLevelFinished').addOnce((levelIndex, newStars, newPoints) => { this.onLevelFinished(newStars, newPoints); });
    G.sb('onDailyChallengeWon').addOnce(() => { this.onLevelFinished(); });
    G.sb('onLevelFailed').addOnce(() => { this.onLevelFailed(); });
    G.sb('onHandlePurchase').add((product, productData) => {
      this.handlePurchase(product, productData);
    });

    if (extraData) {
      if (extraData.requestHelp) {
        this.insertRequestHelpData(extraData.requestHelp);
      }
    }

    // track missionStarted event
    DDNA.tracking.collectEvent('missionStarted', this.getMissionStartParams());
  }

  /**
  * get a copy of the missionStartParams Object
  * @returns {Object}
  */
  getMissionStartParams() {
    const paramObj = {};
    Object.assign(paramObj, this._missionStartParams);
    return paramObj;
  }

  /**
  * get a copy of the missionEndParams Object
  * @returns {Object}
  */
  getMissionFailParams() {
    const paramObj = this.getMissionStartParams();
    Object.assign(paramObj, this._missionEndBaseParams);
    Object.assign(paramObj, this._missionFailParams);
    return paramObj;
  }

  getMissionWinParams() {
    const paramObj = this.getMissionStartParams();
    Object.assign(paramObj, this._missionEndBaseParams);
    Object.assign(paramObj, this._missionWinParams);
  }

  /**
  * get the associated parameters object by parameter name
  * @param {string} paramName name of parameter to lookup
  * @returns {Object}
  */
  getParamObjByParamName(paramName) {
    if (this._missionStartParams[paramName] != null) return this._missionStartParams;
    if (this._missionEndBaseParams[paramName] != null) return this._missionEndBaseParams;
    if (this._missionFailParams[paramName] != null) return this._missionFailParams;
    if (this._missionWinParams[paramName] != null) return this._missionWinParams;
    return null;
  }

  /**
  * set a mission parameter value, looks up correct paramObj
  * @param {string} paramName name of parameter to lookup
  * @param {*} value value to assign
  */
  setMissionParameter(paramName, value) {
    const paramObj = this.getParamObjByParamName(paramName);
    paramObj[paramName] = value;
  }

  /**
  * add to a mission parameter value, looks up correct paramObj
  * @param {string} paramName name of parameter to lookup
  * @param {*} value value to assign
  */
  addToMissionParameter(paramName, value) {
    const paramObj = this.getParamObjByParamName(paramName);
    paramObj[paramName] += value;
  }

  /**
  * track booster use
  * @param {number} boostNum id # of booster used
  * @param {boolean} isPreBooster is a prelevel booster
  * @param {boolean} isOutOfMovesBooster is triggered from out of moves window
  */
  onBoosterUse(boostNum, isPreBooster, isOutOfMovesBooster = false) {
    if (!DDNA.tracking.enabled) return;

    if (isOutOfMovesBooster) { // booster used from out of moves window
      if (boostNum === 5 || boostNum === 6) {
        this.addToMissionParameter('mLevelExtraMovesUsed', 1);
      }
    } else if (!isPreBooster) { // in level boosters
      if (boostNum === 1) {
        this.addToMissionParameter('mSwapBoosterUsed', 1);
      } else if (boostNum === 2) {
        this.addToMissionParameter('mRemoverBoosterUsed', 1);
      } else if (boostNum === 3) {
        this.addToMissionParameter('mHorizontalBoosterUsed', 1);
      } else if (boostNum === 4) {
        this.addToMissionParameter('mVerticalBoosterUsed', 1);
      } else if (boostNum === 5 || boostNum === 6) {
        this.addToMissionParameter('mLevelExtraMovesUsed', 1);
      }

      // track booster use when low on moves
      if (G.lvl.moves <= 3) {
        this.addToMissionParameter('mNumBoostersUsedLossAversion', 1);
      }
    } else { // pre boosters
      // eslint-disable-next-line no-lonely-if
      if (boostNum === 5 || boostNum === 6) {
        this.addToMissionParameter('mExtraMovesUsed', 1);
      } else if (boostNum === 7) {
        this.addToMissionParameter('mSpecialTileUsed', 1);
      } else if (boostNum === 8) {
        this.addToMissionParameter('mBombBoosterUsed', 1);
      }
    }
  }

  /**
   * Puts in the data from requestHelp
   * reqHelpData = {
   *  mRequestHelpUniqueFriends: number
   *  mRequestHelpMessages: number
   *  mRequestHelpIsBot: boolean
   *  mRequestHelpBoosterForLevel: number
   * }
   * @param {Object} reqHelpData
   */
  insertRequestHelpData(reqHelpData) {
    this.setMissionParameter('mRequestHelpUniqueFriends', reqHelpData.mRequestHelpUniqueFriends);
    this.setMissionParameter('mRequestHelpMessages', reqHelpData.mRequestHelpMessages);
    this.setMissionParameter('mRequestHelpIsBot', reqHelpData.mRequestHelpIsBot);
    this.setMissionParameter('mRequestHelpBoosterForLevel', reqHelpData.mRequestHelpBoosterForLevel);
  }

  /**
  * handle in level purchases
  * @param {Object} product product Object
  * @param {Object} productData productData Object
  */
  handlePurchase(product, productData) {
    if (!DDNA.tracking.enabled) return;
    if (!productData || !productData.giftData) return;

    // check all gifts received in the purchase
    let giftType; let giftAmount;
    for (let i = 0; i < productData.giftData.length; i++) {
      giftType = productData.giftData[i][0];
      giftAmount = productData.giftData[i][1];

      if (giftType === 'coin') {
        this.addToMissionParameter('mCoinsBoughtWithRealMoney', giftAmount);
      } else if (giftType.indexOf('booster') === 0) {
        this.addToMissionParameter('mBoostersBoughtWithRealMoney', giftAmount);
      }
    }
  }

  /**
  * listen for goal achieved event and update related parameters
  */
  onGoalAchieved() {
    if (!DDNA.tracking.enabled) return;

    // console.log("DDNA MISSION onGoalAchieved()");
    this.setMissionParameter('mMovesMade', G.lvl.movesMade);
    this.setMissionParameter('mMovesLeft', G.lvl.moves);
    this.setMissionParameter('mScoreBeforeEndSequence', G.lvl.points);
  }

  /**
  * listen for level won event
  */
  onLevelFinished(newStars, newPoints) {
    if (!DDNA.tracking.enabled) return;

    const levelEconTracker = LevelEconomyTracker.getInstance();

    // console.log("DDNA MISSION onLevelFinished()");
    this.setMissionParameter('mCoinsSpent', levelEconTracker.getLevelCoinsSpent());
    this.setMissionParameter('mStars', newStars);
    this.setMissionParameter('mScore', newPoints);
    this.setMissionParameter('mLossAversionCoinsSpent', levelEconTracker.getLossAversionCoinsSpent());
    this.setMissionParameter('mCoinsLevelEnd', DDNA.transactionHelper.getCoinsBySource('LevelEnd'));
    this.setMissionParameter('mCoinsChests', DDNA.transactionHelper.getCoinsBySource('GameplayChest'));
    this.setMissionParameter('mAdsWatchedWheel', levelEconTracker.getRewardedAdsWatched());
    this.setMissionParameter('mAdsWatchedDoubleReward', levelEconTracker.getRewardDoubled());
    this.setMissionParameter('mNumWheelSpins', levelEconTracker.getWheelSpins());
    if (G.saveState.data.fortuneCookieData) {
      this.setMissionParameter('fortuneCookieTokenSpawn', G.saveState.fortuneCookieDataManager.timesSpawned);
      this.setMissionParameter('fortuneCookieTokenCollected', G.saveState.fortuneCookieDataManager.timesCollected);
      this.setMissionParameter('fortuneCookieCategoryRejected', G.saveState.fortuneCookieDataManager.timesDismissed);
      this.setMissionParameter('fortuneCookieEventRejected', G.saveState.fortuneCookieDataManager.timesFullDimiss);
      this.setMissionParameter('fortuneCookieCategorySelected', G.saveState.fortuneCookieDataManager.timesOpened);
      this.setMissionParameter('fortuneCookieShared', G.saveState.fortuneCookieDataManager.timesShared);
    }

    // track missionCompleted event
    DDNA.tracking.collectEvent('missionCompleted', this.getMissionStartParams());
  }

  /**
  * listen for level failed event
  */
  onLevelFailed() {
    if (!DDNA.tracking.enabled) return;

    const levelEconTracker = LevelEconomyTracker.getInstance();

    // console.log("DDNA MISSION onLevelFailed()");
    this.setMissionParameter('mMovesMade', G.lvl.movesMade);
    this.setMissionParameter('mMovesLeft', G.lvl.moves);
    this.setMissionParameter('mCoinsSpent', levelEconTracker.getLevelCoinsSpent());
    this.setMissionParameter('mStars', 0);
    this.setMissionParameter('mScore', G.lvl.points);
    this.setMissionParameter('mLossAversionCoinsSpent', levelEconTracker.getLossAversionCoinsSpent());
    if (G.saveState.data.fortuneCookieData) {
      this.setMissionParameter('fortuneCookieTokenSpawn', G.saveState.fortuneCookieDataManager.timesSpawned);
      this.setMissionParameter('fortuneCookieTokenCollected', G.saveState.fortuneCookieDataManager.timesCollected);
    }

    // track missionFailed event
    DDNA.tracking.collectEvent('missionFailed', this.getMissionFailParams());
  }
}

// create global references
if (!window.DDNA) window.DDNA = {};
DDNA.missionTracker = new DDNAMissionTracker();
